import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { notificar } from '../utils/notify';
import '../styles/fmp.css';

const Admconsultas = () => {
  const [configuracoes, setConfiguracoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [executando, setExecutando] = useState({});
  const [executandoTodas, setExecutandoTodas] = useState(false);

  const navigate = useNavigate();

  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // Carregar configurações
  const carregarConfiguracoes = async () => {
    setCarregando(true);
    const token = localStorage.getItem('access');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/admin/consultas/listar/`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setConfiguracoes(response.data.configuracoes);
      }

    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      setMensagem('Erro ao carregar configurações');
    } finally {
      setCarregando(false);
    }
  };

  // Executar consulta individual
  const executarConsulta = async (endpoint, nome) => {
    setExecutando(prev => ({ ...prev, [endpoint]: true }));
    const token = localStorage.getItem('access');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/admin/consultas/executar/`,
        { endpoint },
        {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 300000 // 5 minutos
        }
      );

      if (response.data.success) {
        setMensagem(`${nome}: ${response.data.total_registros} registros em ${response.data.tempo_execucao}`);
        carregarConfiguracoes(); // Recarregar para atualizar contadores
      }

    } catch (error) {
      console.error('Erro ao executar consulta:', error);
      setMensagem(`Erro ao executar ${nome}`);
    } finally {
      setExecutando(prev => ({ ...prev, [endpoint]: false }));
    }
  };

  // Executar todas as consultas
  const executarTodasConsultas = async () => {
    if (!window.confirm('Deseja executar TODAS as consultas? Isso pode demorar vários minutos.')) {
      return;
    }

    setExecutandoTodas(true);
    setMensagem('Executando todas as consultas... Aguarde...');
    const token = localStorage.getItem('access');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/admin/consultas/executar-todas/`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 600000 // 10 minutos
        }
      );

      if (response.data.success) {
        const resultados = response.data.resultados;
        const sucesso = Object.values(resultados).filter(r => r.status === 'sucesso').length;
        const erro = Object.values(resultados).filter(r => r.status === 'erro').length;
        
        setMensagem(`Concluído em ${response.data.tempo_total}: ${sucesso} sucesso, ${erro} erros`);
        carregarConfiguracoes();
      }

    } catch (error) {
      console.error('Erro ao executar todas:', error);
      setMensagem('Erro ao executar consultas');
    } finally {
      setExecutandoTodas(false);
    }
  };

  // Carregar ao montar
  useEffect(() => {
    carregarConfiguracoes();
    
    // Auto-refresh a cada 30 segundos
    const interval = setInterval(carregarConfiguracoes, 30000);
    return () => clearInterval(interval);
  }, []);

  // Formatar data
  const formatarData = (data) => {
    if (!data) return 'Nunca';
    return new Date(data).toLocaleString('pt-BR');
  };

  // Badge de status
  const renderStatusBadge = (config) => {
    if (executando[config.endpoint]) {
      return <span className="badge bg-warning text-dark">Executando...</span>;
    }
    if (!config.ativo) {
      return <span className="badge bg-secondary">Inativo</span>;
    }
    if (config.status === 'aguardando') {
      return <span className="badge bg-info">Aguardando</span>;
    }
    if (config.status === 'executando') {
      return <span className="badge bg-warning text-dark">Executando</span>;
    }
    if (config.status === 'erro') {
      return <span className="badge bg-danger">Erro</span>;
    }
    return <span className="badge bg-success">OK</span>;
  };

  return (
    <div className="das-page">
      <div className="container-fluid mt-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="titulo-pagina m-0">
                      <i className="fas fa-cogs header-icon me-2"></i>
                      Administração de Consultas - API Omie
                    </h2>
                    <p className="mb-0 mt-2 opacity-75">
                      <i className="fas fa-info-circle me-2"></i>
                      Gerencie as consultas automáticas à API Omie e visualize o status do banco de dados
                    </p>
                  </div>
                  
                  <div className="text-end">
                    <button 
                      className="btn btn-light btn-sm me-2"
                      onClick={carregarConfiguracoes}
                      disabled={carregando}
                    >
                      <i className="fas fa-sync-alt me-1"></i>
                      {carregando ? 'Atualizando...' : 'Atualizar'}
                    </button>
                    
                    <button 
                      className="btn btn-warning btn-sm"
                      onClick={executarTodasConsultas}
                      disabled={executandoTodas || Object.values(executando).some(v => v)}
                    >
                      <i className="fas fa-rocket me-1"></i>
                      {executandoTodas ? 'Executando Todas...' : 'Executar Todas'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* Tabela de Consultas */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-database me-2 text-primary"></i>
                  Consultas Configuradas
                </h5>
              </div>
              
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th width="5%" className="text-center">#</th>
                        <th width="20%">Nome da Consulta</th>
                        <th width="15%">Tabela</th>
                        <th width="10%" className="text-center">Intervalo</th>
                        <th width="12%" className="text-center">Total Registros</th>
                        <th width="15%">Última Execução</th>
                        <th width="10%" className="text-center">Status</th>
                        <th width="13%" className="text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {carregando ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5">
                            <div className="spinner-border text-primary"></div>
                            <p className="mt-2 text-muted">Carregando configurações...</p>
                          </td>
                        </tr>
                      ) : configuracoes.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5 text-muted">
                            <i className="fas fa-inbox fa-3x mb-3"></i>
                            <p>Nenhuma configuração encontrada</p>
                          </td>
                        </tr>
                      ) : (
                        configuracoes.map((config) => (
                          <tr key={config.id}>
                            <td className="text-center">
                              <strong>{config.id}</strong>
                            </td>
                            
                            <td>
                              <div className="d-flex align-items-center">
                                <i className="fas fa-database text-primary me-2"></i>
                                <div>
                                  <strong>{config.nome}</strong>
                                  <br />
                                  <small className="text-muted">{config.endpoint}</small>
                                </div>
                              </div>
                            </td>
                            
                            <td>
                              <code className="text-info">{config.tabela}</code>
                            </td>
                            
                            <td className="text-center">
                              <span className="badge bg-light text-dark">
                                <i className="fas fa-clock me-1"></i>
                                {config.intervalo_minutos} min
                              </span>
                            </td>
                            
                            <td className="text-center">
                              <span className="badge bg-primary" style={{fontSize: '14px'}}>
                                {config.total_registros.toLocaleString('pt-BR')}
                              </span>
                            </td>
                            
                            <td>
                              <small className="text-muted">
                                {formatarData(config.ultima_execucao)}
                              </small>
                            </td>
                            
                            <td className="text-center">
                              {renderStatusBadge(config)}
                            </td>
                            
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => executarConsulta(config.endpoint, config.nome)}
                                disabled={executando[config.endpoint] || executandoTodas}
                                title="Executar agora"
                              >
                                {executando[config.endpoint] ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                    Executando
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-play me-1"></i>
                                    Executar
                                  </>
                                )}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="card-footer bg-light">
                <div className="row text-center">
                  <div className="col-md-3">
                    <small className="text-muted d-block">Total de Consultas</small>
                    <h5 className="text-primary mb-0">{configuracoes.length}</h5>
                  </div>
                  <div className="col-md-3">
                    <small className="text-muted d-block">Consultas Ativas</small>
                    <h5 className="text-success mb-0">
                      {configuracoes.filter(c => c.ativo).length}
                    </h5>
                  </div>
                  <div className="col-md-3">
                    <small className="text-muted d-block">Total de Registros</small>
                    <h5 className="text-info mb-0">
                      {configuracoes.reduce((sum, c) => sum + c.total_registros, 0).toLocaleString('pt-BR')}
                    </h5>
                  </div>
                  <div className="col-md-3">
                    <small className="text-muted d-block">Última Atualização</small>
                    <h6 className="text-muted mb-0">
                      <i className="fas fa-sync-alt me-1"></i>
                      {new Date().toLocaleTimeString('pt-BR')}
                    </h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Informações Adicionais */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0 bg-light">
              <div className="card-body">
                <h6 className="text-muted mb-3">
                  <i className="fas fa-info-circle me-2"></i>
                  Informações Importantes
                </h6>
                <ul className="mb-0">
                  <li className="text-muted mb-2">
                    <strong>Intervalo:</strong> Tempo entre cada execução automática da consulta
                  </li>
                  <li className="text-muted mb-2">
                    <strong>Execução Manual:</strong> Clique em "Executar" para atualizar imediatamente
                  </li>
                  <li className="text-muted mb-2">
                    <strong>Executar Todas:</strong> Executa todas as consultas sequencialmente (pode demorar)
                  </li>
                  <li className="text-muted">
                    <strong>Auto-Refresh:</strong> Esta página atualiza automaticamente a cada 30 segundos
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admconsultas;